import type { LibraryData, LibrarySongData, ManifestEntry } from "@common/entities/library-song";

const DB_NAME = "setlist-song-library";
const DB_VERSION = 2;
const SONGS_STORE = "songs";
const LIBRARIES_STORE = "libraries";

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(SONGS_STORE)) {
        const store = db.createObjectStore(SONGS_STORE, { keyPath: "id" });
        store.createIndex("title", "title", { unique: false });
      }
      if (!db.objectStoreNames.contains(LIBRARIES_STORE)) {
        db.createObjectStore(LIBRARIES_STORE, { keyPath: "id" });
      }
    };
  });
}

function toPlainObject<T>(data: T): T {
  return JSON.parse(JSON.stringify(data));
}

export async function getAllCachedSongs(): Promise<LibrarySongData[]> {
  const db = await openDatabase();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(SONGS_STORE, "readonly");
    const store = tx.objectStore(SONGS_STORE);
    const request = store.getAll();
    request.onerror = () => { db.close(); reject(request.error); };
    request.onsuccess = () => { db.close(); resolve(request.result); };
  });
}

export async function getCachedSong(
  id: string,
): Promise<LibrarySongData | undefined> {
  const db = await openDatabase();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(SONGS_STORE, "readonly");
    const store = tx.objectStore(SONGS_STORE);
    const request = store.get(id);
    request.onerror = () => { db.close(); reject(request.error); };
    request.onsuccess = () => { db.close(); resolve(request.result ?? undefined); };
  });
}

export async function saveCachedSong(song: LibrarySongData): Promise<void> {
  const db = await openDatabase();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(SONGS_STORE, "readwrite");
    const store = tx.objectStore(SONGS_STORE);
    store.put(toPlainObject(song));
    tx.oncomplete = () => { db.close(); resolve(); };
    tx.onerror = () => { db.close(); reject(tx.error); };
  });
}

export async function deleteCachedSong(id: string): Promise<void> {
  const db = await openDatabase();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(SONGS_STORE, "readwrite");
    const store = tx.objectStore(SONGS_STORE);
    store.delete(id);
    tx.oncomplete = () => { db.close(); resolve(); };
    tx.onerror = () => { db.close(); reject(tx.error); };
  });
}

/**
 * Diffs the remote manifest against the local IndexedDB cache.
 *
 * Returns the IDs that need a full data fetch (new or updated remotely)
 * and the IDs that should be deleted locally (removed remotely).
 */
export async function diffManifest(
  manifest: ManifestEntry[],
): Promise<{ toFetch: string[]; toDelete: string[] }> {
  const cached = await getAllCachedSongs();
  const cachedMap = new Map(cached.map((s) => [s.id, s]));
  const manifestIds = new Set(manifest.map((e) => e.id));

  const toFetch: string[] = [];
  for (const entry of manifest) {
    const local = cachedMap.get(entry.id);
    if (!local || entry.updatedAt > local.updatedAt) {
      toFetch.push(entry.id);
    }
  }

  const toDelete: string[] = [];
  for (const local of cached) {
    if (!manifestIds.has(local.id)) {
      toDelete.push(local.id);
    }
  }

  return { toFetch, toDelete };
}

/**
 * Applies a batch of fetched songs and deletions to the IndexedDB cache.
 */
export async function applySyncResult(
  fetchedSongs: LibrarySongData[],
  deletedIds: string[],
): Promise<void> {
  const db = await openDatabase();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(SONGS_STORE, "readwrite");
    const store = tx.objectStore(SONGS_STORE);

    for (const song of fetchedSongs) {
      store.put(toPlainObject(song));
    }
    for (const id of deletedIds) {
      store.delete(id);
    }

    tx.oncomplete = () => { db.close(); resolve(); };
    tx.onerror = () => { db.close(); reject(tx.error); };
  });
}

// --------------- Library cache ---------------

export async function getCachedLibraries(): Promise<LibraryData[]> {
  const db = await openDatabase();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(LIBRARIES_STORE, "readonly");
    const store = tx.objectStore(LIBRARIES_STORE);
    const request = store.getAll();
    request.onerror = () => { db.close(); reject(request.error); };
    request.onsuccess = () => { db.close(); resolve(request.result); };
  });
}

export async function saveCachedLibraries(
  libraries: LibraryData[],
): Promise<void> {
  const db = await openDatabase();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(LIBRARIES_STORE, "readwrite");
    const store = tx.objectStore(LIBRARIES_STORE);
    store.clear();
    for (const lib of libraries) {
      store.put(toPlainObject(lib));
    }
    tx.oncomplete = () => { db.close(); resolve(); };
    tx.onerror = () => { db.close(); reject(tx.error); };
  });
}
